import { Box } from '@mui/material'
import { createFileRoute, redirect } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { EntityMappingTab } from '@/components/EntityMappingTab'
import { ErrorComponent } from '@/components/ErrorComponent'
import { LoadingComponent } from '@/components/LoadingComponent'
import {
	getEntityTabs,
	loadEntityTabRoute,
	resolveEntity,
	validateEntityTabSearch,
} from '@/entity-config-runtime'
import {
	canReadEntityOrTab,
	firstReadableTab,
} from '@/integrations/casl/route-ability'
import { getEntityTabLabel } from '@/lib/entity-route-i18n'

export const Route = createFileRoute('/_authenticated/$entity/$entityId/$tab')({
	beforeLoad: ({ params, context }) => {
		const entity = String(params.entity ?? '')
		const tab = String(params.tab ?? '')
		const entityId = String(params.entityId ?? '')

		if (tab === 'overview') {
			throw redirect({
				to: '/$entity/$entityId/details',
				params: {
					entity,
					entityId,
				},
				replace: true,
			})
		}

		if (!canReadEntityOrTab(context.ability, entity, tab)) {
			const Entity = resolveEntity(entity)
			const fallbackTab = firstReadableTab(
				context.ability,
				entity,
				getEntityTabs(Entity).map((item) => item.key),
			)

			if (fallbackTab) {
				throw redirect({
					to: '/$entity/$entityId/$tab',
					params: {
						entity,
						entityId,
						tab: fallbackTab,
					},
					replace: true,
				})
			}

			throw new Error(
				`Missing read permission for tab "${tab}" on entity "${entity}"`,
			)
		}
	},
	validateSearch: validateEntityTabSearch,
	component: EntityTabRoute,
	staticData: {
		breadcrumb: ({ params }) => {
			const tab = String(params.tab ?? '')
			return { label: getEntityTabLabel(tab) }
		},
	},
	loaderDeps({ search }) {
		return search
	},
	loader({ params, deps, context: { queryClient } }) {
		return loadEntityTabRoute({
			params,
			search: deps,
			context: { queryClient },
		})
	},
	pendingComponent: LoadingComponent,
	errorComponent: ErrorComponent,
})

const scrollableTabs = new Set(['entityFilter'])

function EntityTabRoute() {
	const { entity, tab, entityId } = Route.useParams()
	const isScrollableContent = scrollableTabs.has(tab)

	const content: ReactNode = (
		<EntityMappingTab
			entity={entity}
			tab={tab}
			entityId={entityId}
			key={`${entity}-${tab}`}
		/>
	)

	return (
		<Box
			sx={{
				width: '100%',
				height: '100%',
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}
		>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					pr: 1,
					overflow: isScrollableContent ? 'auto' : 'hidden',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{content}
			</Box>
		</Box>
	)
}
